// Marker SVG factory.
// Vendors → circle + VENDOR_GLYPH (universal awning) tinted with the
//   category color; `information` is a special-case disc.
// Storefront-category vendors → rounded-square + brownstone glyph + slate
//   fill (overrides the category color so brick-and-mortar reads as
//   "structure" and is visually distinct from farmer/maker tents).
// Amenity types (atm / first_aid / restroom / stage) → rounded-square (atm)
//   or circle + per-type glyph.
//
// SECURITY: the returned string is fed to `innerHTML` in `ScaledMarkerLayer`.
// Any value interpolated into the markup MUST be either a hardcoded constant
// or sanitized. The only dynamic inputs are colors (sanitized via
// `sanitizeHexColor`) and `categoryId` (used only as a `Map` key — never
// echoed into HTML). If you add new dynamic inputs, sanitize them here.

import type { Category } from '../../types';
import { legibleOn } from '../../utils/legibleOn';
import {
  amenityFill,
  isAmenityType,
  isStorefrontCategoryId,
  storefrontFill,
} from '../../utils/amenities';
import { sanitizeHexColor } from '../../utils/sanitizeHexColor';
export { sanitizeHexColor };
import { GLYPH_PATHS, GLYPH_DEFAULT } from '../../theme/categoryGlyphs';
import { resolveGlyphKey } from '../../utils/glyphKey';
import { CREAM } from '../../theme/colors';

/** Read a CSS custom property off :root. Falls back to the literal default
 *  in SSR or before the stylesheet loads. Re-reads on every call — callers
 *  are already invoked on theme change because markers rebuild. */
function readVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function tokenCream(): string {
  return readVar('--color-text-on-dark', CREAM);
}

// Amenity color resolution lives in `utils/amenities` so the marker SVG
// builder, list-row avatar, and any future amenity surface all read from
// one source of truth. See `amenityFill(type)`.

function getGlyph(categoryId: string): string {
  return GLYPH_PATHS[categoryId] ?? GLYPH_DEFAULT;
}

export type MarkerType = 'vendor' | 'atm' | 'first_aid' | 'restroom' | 'stage';

export function buildMarkerSvg(
  type: MarkerType,
  categoryId: string,
  categoryColor: string,
): string {
  // Sanitize before any string interpolation. `legibleOn` returns one of two
  // hardcoded constants but we re-sanitize defensively in case the helper is
  // ever changed to derive the color from input.
  // Amenity types and storefront-category vendors override the caller-supplied
  // color with a fixed palette token so they look identical regardless of
  // what color the Categories sheet happens to specify.
  const isAmenity = isAmenityType(type);
  const isStorefront = !isAmenity && isStorefrontCategoryId(categoryId);
  const fillSource = isAmenity
    ? amenityFill(type)
    : isStorefront
      ? storefrontFill()
      : categoryColor;
  const safeColor = sanitizeHexColor(fillSource);
  const fg = sanitizeHexColor(legibleOn(safeColor));
  const glyph = getGlyph(resolveGlyphKey(type, categoryId))
    .replaceAll('FG', fg).replaceAll('BG', safeColor);
  const cream = tokenCream();
  // ATM and storefront-category vendors render as a rounded square; all
  // other types use a circle.
  const isRoundedSquare = type === 'atm' || isStorefront;
  const shape = isRoundedSquare
    ? `<rect x="3" y="3" width="34" height="34" rx="8" ry="8" fill="${safeColor}" stroke="${cream}" stroke-width="2.5"/>`
    : `<circle cx="20" cy="20" r="17" fill="${safeColor}" stroke="${cream}" stroke-width="2.5"/>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" aria-hidden="true">
    <ellipse cx="20" cy="37.5" rx="12" ry="1.4" fill="rgba(57,60,61,0.22)"/>
    ${shape}
    ${glyph}
  </svg>`;
}

export function pickCategoryColor(categories: Category[], categoryId: string): string {
  const cat = categories.find(c => c.id.toLowerCase() === categoryId.toLowerCase());
  return sanitizeHexColor(cat?.color);
}
